import * as React from 'react';

/* ============================================================================
   TrajetoMapa - desenha o CAMINHO do aparelho, e não só onde ele está agora.

   O rastro já existia como lista: cem registros de latitude/longitude com data,
   um embaixo do outro, e o mapa mostrando um pino de cada vez. Dá para saber
   onde o aparelho estava às 16:17 clicando na linha das 16:17 - mas não dá para
   ver que ele fez uma volta, que ficou parado a tarde inteira ou que saiu da
   área. Essa é a pergunta que leva alguém a abrir a geolocalização, e ela só se
   responde vendo os pontos LIGADOS, na ordem em que aconteceram.

   COMO SE ENCAIXA. É filho do `GoogleMap`, que clona os filhos injetando `map`
   (ver components/GoogleMaps/GoogleMap.tsx). Nenhum arquivo do Google foi
   alterado: este componente só usa a instância que já é passada.

   ORDEM CRONOLÓGICA, e isso importa: a API devolve do mais recente para o mais
   antigo (`direcao=desc`). Desenhar nessa ordem daria a mesma linha percorrida
   ao contrário - e a numeração diria que o aparelho começou onde terminou.
   Aqui a lista é reordenada por data antes de virar caminho.

   POR QUE NEM TODO PONTO GANHA NÚMERO: com cem registros, cem balões numerados
   viram uma mancha e escondem justamente a forma do trajeto. Até 25 pontos cada
   um é numerado; acima disso os intermediários viram pontos pequenos e só
   início e fim ficam marcados. O número continua na lista ao lado, que é onde
   se lê data e endereço.
   ============================================================================ */

export type PontoTrajeto = {
  lat: number;
  lng: number;
  /** Data/hora do registro, usada para ordenar e no balão de informação. */
  quando?: string;
  endereco?: string;
  /** Identificador da linha na lista, para casar clique do mapa com a lista. */
  chave?: string;
};

/** Acima disto, numerar cada parada vira mancha em vez de informação. */
const LIMITE_NUMERADO = 25;

export default function TrajetoMapa({
  map,
  pontos,
  ativo,
  aoClicarPonto,
  reproduzindo = false,
  velocidade = 1,
  aoTerminar,
}: {
  /** Injetado pelo GoogleMap ao clonar o filho. */
  map?: google.maps.Map;
  pontos: PontoTrajeto[];
  /** Chave do ponto destacado (o selecionado na lista). */
  ativo?: string | null;
  aoClicarPonto?: (chave: string) => void;
  /** Anda o percurso do inicio ao fim. */
  reproduzindo?: boolean;
  /** Multiplicador de velocidade (1x, 2x, 4x...). */
  velocidade?: number;
  aoTerminar?: () => void;
}) {
  const desenhos = React.useRef<Array<google.maps.Polyline | google.maps.Marker>>([]);
  /* O caminho ja ordenado fica acessivel ao efeito da reproducao sem reordenar
     a mesma lista duas vezes. */
  const caminhoRef = React.useRef<Array<{ lat: number; lng: number }>>([]);

  /* `pontos` costuma vir de um `map()` no pai, ou seja, array novo a cada
     render. Redesenhar tudo a cada render pisca o mapa inteiro; a assinatura
     abaixo muda só quando as COORDENADAS mudam de verdade. */
  const assinatura = React.useMemo(
    () => pontos.map((p) => `${p.lat},${p.lng}`).join('|'),
    [pontos]
  );

  React.useEffect(() => {
    if (!map || pontos.length === 0) return undefined;

    /* Do mais antigo para o mais novo. Sem data (registro antigo, campo vazio)
       o ponto mantém a posição relativa que tinha - `sort` estável no V8 -, em
       vez de ser jogado para uma ponta e inventar um trecho de caminho. */
    const emOrdem = [...pontos].sort((a, b) => {
      const ta = new Date(a.quando || '').getTime();
      const tb = new Date(b.quando || '').getTime();
      if (!Number.isFinite(ta) || !Number.isFinite(tb)) return 0;
      return ta - tb;
    });

    const caminho = emOrdem.map((p) => ({ lat: p.lat, lng: p.lng }));
    const numerar = emOrdem.length <= LIMITE_NUMERADO;

    const linha = new google.maps.Polyline({
      path: caminho,
      map,
      geodesic: true,
      strokeColor: '#2563eb',
      strokeOpacity: 0.9,
      strokeWeight: 4,
      /* As setas dizem o SENTIDO do percurso. Sem elas a linha é ambígua: o
         mesmo traço serve para ida e para volta, e quem olha não sabe de onde
         para onde o aparelho foi. */
      icons: [
        {
          icon: { path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.4, strokeColor: '#1d4ed8' },
          offset: '0',
          repeat: '90px',
        },
      ],
    });

    const marcadores = emOrdem.map((p, i) => {
      const primeiro = i === 0;
      const ultimo = i === emOrdem.length - 1;
      const destacado = Boolean(ativo && p.chave && String(p.chave) === String(ativo));

      /* Verde onde começou, laranja onde terminou, azul no meio. Cor sozinha
         não basta - por isso início e fim também são MAIORES e mantêm o número,
         e o `title` diz em palavras qual é qual. */
      const cor = primeiro ? '#15803d' : ultimo ? '#c2410c' : '#2563eb';
      const grande = primeiro || ultimo || destacado;

      return new google.maps.Marker({
        position: { lat: p.lat, lng: p.lng },
        map,
        zIndex: grande ? 3 : 1,
        title: [
          primeiro ? 'Início do trajeto' : ultimo ? 'Ponto mais recente' : `Parada ${i + 1}`,
          p.quando ? new Date(p.quando).toLocaleString('pt-BR') : null,
          p.endereco || null,
        ]
          .filter(Boolean)
          .join('\n'),
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: grande ? 11 : numerar ? 9 : 5,
          fillColor: destacado ? '#0f172a' : cor,
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: grande ? 2.5 : 1.5,
        },
        label:
          numerar || grande
            ? { text: String(i + 1), color: '#ffffff', fontSize: '10px', fontWeight: '700' }
            : undefined,
      });
    });

    if (aoClicarPonto) {
      marcadores.forEach((m, i) => {
        const chave = emOrdem[i]?.chave;
        if (chave) m.addListener('click', () => aoClicarPonto(String(chave)));
      });
    }

    /* Enquadra TODO o trajeto. Sem isto o mapa fica no zoom do último ponto e a
       linha sai da tela - o recurso existiria e não seria visto. */
    const limites = new google.maps.LatLngBounds();
    caminho.forEach((c) => limites.extend(c));
    map.fitBounds(limites, 32);

    /* Um ponto só (aparelho parado): `fitBounds` de um retângulo sem área
       aproxima ao máximo e a tela vira uma textura sem referência nenhuma. */
    if (caminho.length === 1) map.setZoom(17);

    caminhoRef.current = caminho;
    desenhos.current = [linha, ...marcadores];

    return () => {
      desenhos.current.forEach((d) => d.setMap(null));
      desenhos.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, assinatura, ativo]);

  /* ------------------------------------------------------ ANDAR O PERCURSO

     "Ver toda a rota andando no mapa": um marcador percorre o caminho do
     inicio ao fim, como no sistema de ronda de referencia.

     INTERPOLA ENTRE OS PONTOS em vez de saltar de registro em registro. Os
     logs chegam a cada ~15 minutos; pular de um para o outro daria uma
     sequencia de teleportes, e o que se quer ver e o sentido e o ritmo do
     deslocamento. `spherical.interpolate` da a posicao intermediaria sobre o
     arco - a biblioteca `geometry` ja e carregada pelo Wrapper em main.tsx.

     O TEMPO E PROPORCIONAL A DISTANCIA de cada trecho, e nao igual por trecho:
     com tempo fixo, um salto de 8 km entre dois logs levaria o mesmo que 40
     metros, e a animacao mentiria sobre o percurso.

     `requestAnimationFrame` e nao `setInterval`: o navegador pausa o rAF
     quando a aba sai de foco, entao a animacao nao corre escondida gastando
     bateria. */
  React.useEffect(() => {
    if (!map || !reproduzindo) return undefined;

    const caminho = caminhoRef.current;
    if (caminho.length < 2) {
      aoTerminar?.();
      return undefined;
    }

    const esferica = google.maps.geometry?.spherical;
    const vertices = caminho.map((c) => new google.maps.LatLng(c.lat, c.lng));

    const trechos = vertices.slice(0, -1).map((p, i) => ({
      de: p,
      para: vertices[i + 1],
      metros: Math.max(esferica ? esferica.computeDistanceBetween(p, vertices[i + 1]) : 1, 1),
    }));
    const total = trechos.reduce((soma, t) => soma + t.metros, 0);

    /* 9 segundos para o percurso inteiro em 1x: curto o bastante para nao
       cansar, longo o bastante para o olho acompanhar a forma do trajeto. */
    const duracao = Math.max(1200, 9000 / Math.max(velocidade, 0.25));

    const movel = new google.maps.Marker({
      position: vertices[0],
      map,
      zIndex: 9,
      title: 'Percurso',
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 9,
        fillColor: '#ea580c',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3,
      },
    });

    /* Rastro do que ja foi percorrido, por cima da linha azul: sem ele, num
       trajeto que passa duas vezes pela mesma rua nao da para saber em que
       volta a animacao esta. */
    const percorrido = new google.maps.Polyline({
      path: [vertices[0]],
      map,
      strokeColor: '#ea580c',
      strokeOpacity: 1,
      strokeWeight: 5,
      zIndex: 8,
    });

    let quadro = 0;
    let inicio = 0;

    const passo = (agora: number) => {
      if (!inicio) inicio = agora;
      const fracao = Math.min(1, (agora - inicio) / duracao);

      /* Quantos metros ja foram andados; converte para o trecho e a fracao
         DENTRO dele - e o que mantem a velocidade constante em distancia. */
      let restante = fracao * total;
      let posicao = vertices[vertices.length - 1];
      for (let i = 0; i < trechos.length; i += 1) {
        if (restante <= trechos[i].metros) {
          const dentro = restante / trechos[i].metros;
          posicao = esferica ? esferica.interpolate(trechos[i].de, trechos[i].para, dentro) : trechos[i].para;
          break;
        }
        restante -= trechos[i].metros;
      }

      movel.setPosition(posicao);
      percorrido.getPath().push(posicao);

      if (fracao < 1) quadro = requestAnimationFrame(passo);
      else aoTerminar?.();
    };

    quadro = requestAnimationFrame(passo);

    return () => {
      cancelAnimationFrame(quadro);
      movel.setMap(null);
      percorrido.setMap(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, reproduzindo, velocidade, assinatura]);

  return null;
}